'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { X, Settings2, CheckCircle2, AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';

/**
 * Modal global de configuración de moneda.
 * Lee y guarda las tasas a través de CurrencyContext, por lo que el cambio
 * se refleja de inmediato en todo el CRM.
 */
const CurrencyModal = ({ isOpen, onClose, onSaved }) => {
    const { rates, saveRates } = useCurrency();
    const [form, setForm] = useState(rates);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    // Sincronizar el formulario cada vez que se abre
    useEffect(() => {
        if (isOpen) {
            setForm(rates);
            setStatus(null);
            setErrorMessage('');
        }
    }, [isOpen, rates]);

    // Cerrar con Escape
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !saving) onClose?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, saving, onClose]);

    const buy = Number(String(form.BUY_EXCHANGE_RATE).replace(',', '.'));
    const sell = Number(String(form.SELL_EXCHANGE_RATE).replace(',', '.'));
    const isValid = Number.isFinite(buy) && buy > 0 && Number.isFinite(sell) && sell > 0;
    const orderWarning = isValid && buy >= sell;

    const preview = useMemo(() => {
        if (!Number.isFinite(sell) || sell <= 0) return null;
        return {
            usdToGtq: (1000 * sell).toLocaleString('es-GT', { maximumFractionDigits: 0 }),
            gtqToUsd: (1000 / sell).toFixed(2),
        };
    }, [sell]);

    const handleSave = async () => {
        if (!isValid || saving) return;
        try {
            setSaving(true);
            setStatus(null);
            setErrorMessage('');
            const saved = await saveRates({
                BUY_EXCHANGE_RATE: form.BUY_EXCHANGE_RATE,
                SELL_EXCHANGE_RATE: form.SELL_EXCHANGE_RATE,
            });
            setStatus('success');
            if (onSaved) onSaved(saved);
            setTimeout(() => {
                onClose?.();
            }, 1200);
        } catch (error) {
            console.error('Error saving exchange rates:', error);
            setStatus('error');
            setErrorMessage('No se pudieron guardar las tasas. Intenta de nuevo.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[999] flex items-center justify-center p-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !saving) onClose?.();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="currency-modal-title"
                className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-slate-200 overflow-hidden"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#714B67]/10 text-[#714B67] flex items-center justify-center shrink-0">
                            <Settings2 size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 id="currency-modal-title" className="text-sm font-black text-slate-800 leading-tight">
                                Configuración de Moneda
                            </h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                Tipo de cambio global del CRM
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50 cursor-pointer shrink-0"
                        aria-label="Cerrar"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    <div className="flex items-start gap-2.5 bg-[#714B67]/5 border border-[#714B67]/15 rounded-xl px-3 py-2.5">
                        <RefreshCw size={14} className="text-[#714B67] mt-0.5 shrink-0" />
                        <p className="text-[11px] leading-relaxed text-slate-600">
                            Estas tasas se aplican a <strong className="font-bold text-slate-700">todo el CRM</strong>:
                            dashboard, proyectos, propiedades y calculadora ROI.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <label htmlFor="buy-rate" className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                Tasa de compra
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">Q</span>
                                <input
                                    id="buy-rate"
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    value={form.BUY_EXCHANGE_RATE}
                                    onChange={(event) => setForm((prev) => ({ ...prev, BUY_EXCHANGE_RATE: event.target.value }))}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-7 pr-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#714B67]/20 focus:border-[#714B67] transition-all"
                                />
                            </div>
                            <p className="text-[10px] text-slate-400">GTQ → USD (valoración)</p>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="sell-rate" className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                Tasa de venta
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">Q</span>
                                <input
                                    id="sell-rate"
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    value={form.SELL_EXCHANGE_RATE}
                                    onChange={(event) => setForm((prev) => ({ ...prev, SELL_EXCHANGE_RATE: event.target.value }))}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 pl-7 pr-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#714B67]/20 focus:border-[#714B67] transition-all"
                                />
                            </div>
                            <p className="text-[10px] text-slate-400">USD → GTQ (conversión)</p>
                        </div>
                    </div>

                    {/* Vista previa */}
                    {preview && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                                Referencia rápida
                            </p>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-600">
                                <span>$1,000</span>
                                <ArrowRight size={11} className="text-slate-400" />
                                <span className="text-slate-800 font-bold">Q {preview.usdToGtq}</span>
                                <span className="text-slate-300">|</span>
                                <span>Q1,000</span>
                                <ArrowRight size={11} className="text-slate-400" />
                                <span className="text-slate-800 font-bold">$ {preview.gtqToUsd}</span>
                            </div>
                        </div>
                    )}

                    {!isValid && (
                        <div className="flex items-start gap-2 text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200/80 rounded-xl px-3 py-2">
                            <AlertCircle size={13} className="mt-0.5 shrink-0" />
                            <span>Ambas tasas deben ser números mayores que cero.</span>
                        </div>
                    )}

                    {orderWarning && (
                        <div className="flex items-start gap-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200/80 rounded-xl px-3 py-2">
                            <AlertCircle size={13} className="mt-0.5 shrink-0" />
                            <span>La tasa de compra suele ser menor que la de venta. Revisa los valores.</span>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex items-start gap-2 text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200/80 rounded-xl px-3 py-2">
                            <AlertCircle size={13} className="mt-0.5 shrink-0" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition-all disabled:opacity-50 cursor-pointer"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !isValid}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold text-white shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer ${
                            status === 'success'
                                ? 'bg-emerald-600'
                                : 'bg-[#714B67] hover:bg-[#5a3b52]'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {saving ? (
                            <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                            <CheckCircle2 size={14} />
                        )}
                        <span>{status === 'success' ? 'Guardado' : 'Guardar cambios'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CurrencyModal;
